/**
 * The unconfirmed-address strip, and the one button on it.
 *
 * *Send it again* reissues rather than repeating the last token — whatever
 * happened to the first mail stops being a way in — and the Worker refuses a
 * second inside two minutes, which is what stops this being a mail cannon
 * pointed at an address of somebody's choosing. Where the deployment cannot
 * send at all it says so, because a button that promises a message that is
 * never posted is worse than no button.
 *
 * It is a note and not a modal: an unconfirmed account is not broken. It signs
 * in, downloads the trial and reads everything; what it cannot do is be
 * charged or make us send mail, and both are named here rather than discovered
 * at the checkout button.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { api, ApiError } from '../lib/api';
import type { Me } from '../lib/types';

export function ConfirmStrip({ me, onRefresh }: { me: Me; onRefresh: () => Promise<void> }): React.JSX.Element {
  const [state, setState] = useState<string | null>(null);
  // Only ever set where the deployment cannot post mail. There, the link is the
  // message — the Worker hands it to the one person it is about, in their own
  // session, exactly as an invitation is handed to whoever sent it.
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // An organization's owner meets the refusal first at Billing's *Start the
  // trial* (`startTrial` asks a confirmed address too), so the strip names that
  // rather than a personal licence they are not offered.
  const owned = me.organizations.find((organization) => organization.role === 'owner') ?? null;

  const again = async (): Promise<void> => {
    setBusy(true);
    try {
      const answer = await api.post<{ alreadyVerified: boolean; sent: boolean; url: string | null }>('/me/verify');
      if (answer.alreadyVerified) {
        setState('That address is already confirmed.');
        await onRefresh();
      } else if (answer.sent) {
        setState(`Sent again to ${me.user.email}.`);
      } else if (answer.url) {
        setState('This deployment cannot send email, so here is the link itself.');
        setLink(answer.url);
      } else {
        setState('That could not be sent. Write to support@tryairlock.app.');
      }
    } catch (failure) {
      setState(failure instanceof ApiError ? failure.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="status"
      style={{
        background: color.warnBg,
        boxShadow: `inset 2px 0 0 ${color.yellow}`,
        padding: '11px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap'
      }}
    >
      <span style={{ fontSize: 12.5, flex: 1, minWidth: 260, lineHeight: 1.5, color: color.warnText }}>
        <strong style={{ fontWeight: 600 }}>Confirm {me.user.email}.</strong> We sent a link there. Until it is opened
        you can look around and{' '}
        {owned
          ? `download Airlock, but not start ${owned.name}’s trial, buy seats or invite anybody to one.`
          : 'download the trial, but not buy a licence or invite anybody to a seat.'}
        {state && <span style={{ display: 'block', marginTop: 5 }}>{state}</span>}
        {link && (
          <a href={link} style={{ display: 'block', fontFamily: font.mono, fontSize: 11.5, marginTop: 7, wordBreak: 'break-all' }}>
            {link}
          </a>
        )}
      </span>
      <button
        onClick={() => void again()}
        disabled={busy}
        style={{
          flex: 'none',
          fontFamily: 'inherit',
          fontSize: 12.5,
          color: color.ink,
          background: color.surface,
          border: `1px solid ${color.lineStrong}`,
          padding: '7px 13px',
          borderRadius: radius.md,
          cursor: busy ? 'default' : 'pointer'
        }}
      >
        {busy ? 'Sending' : 'Send it again'}
      </button>
    </div>
  );
}

/**
 * An invitation opened by somebody who is already signed in. A question rather
 * than an action: being added to a tenant is not something to discover
 * afterwards.
 */
export function InviteBar({ onAccept, onDismiss }: { onAccept: () => void; onDismiss: () => void }): React.JSX.Element {
  return (
    <div
      style={{
        background: color.surfaceAlt,
        boxShadow: `inset 2px 0 0 ${color.lineInput}`,
        padding: '11px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap'
      }}
    >
      <span style={{ fontSize: 12.5, flex: 1, minWidth: 240, color: color.inkSubtle }}>
        You have been invited to join an organization on Airlock.
      </span>
      <button
        onClick={onAccept}
        style={{
          flex: 'none',
          fontFamily: 'inherit',
          fontSize: 12.5,
          fontWeight: 500,
          color: '#FFFFFF',
          background: color.ink,
          border: 0,
          padding: '8px 14px',
          borderRadius: radius.md,
          cursor: 'pointer'
        }}
      >
        Accept the invitation
      </button>
      <button
        onClick={onDismiss}
        style={{
          flex: 'none',
          fontFamily: 'inherit',
          fontSize: 12.5,
          color: color.inkSubtle,
          background: 'none',
          border: 0,
          cursor: 'pointer'
        }}
      >
        Not now
      </button>
    </div>
  );
}
